import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ticketGet } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorHandler';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { 
  Globe, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Download,
  ChevronDown,
  FileText
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadStyledXlsx } from '../../utils/exportStyledXlsx';

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const SourceWiseTicketsReport = () => {
  const navigate = useNavigate();
  const [sourceData, setSourceData] = useState([]);
  const [filteredSourceData, setFilteredSourceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);

  // Load report on mount and when pagination changes
  useEffect(() => {
    loadSourceReport();
  }, [currentPage, pageSize]);

  const loadSourceReport = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Build API params
      const params = {
        type: 'TICKET_SOURCE'
      };

      // Call the analytics API
      const response = await ticketGet('/gateway/api/ticket/analytics/report', params);
      
      // Handle different response structures
      // API returns: { status, message, data: [...], success }
      // ticketGet returns the response directly
      let reportData = [];
      
      if (response?.success && response?.data) {
        // Standard ResponseBean format: { success: true, data: [...] }
        reportData = Array.isArray(response.data) ? response.data : [];
      } else if (response?.data?.success && response?.data?.data) {
        // Nested ResponseBean format: { data: { success: true, data: [...] } }
        reportData = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (response?.data) {
        // Direct data array: { data: [...] }
        reportData = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(response)) {
        // Direct array response
        reportData = response;
      }
      
      // Transform data: map 'key' field to 'sourceName' for consistency
      reportData = reportData.map(item => ({
        ...item,
        sourceName: item.key || item.source || item.sourceName || 'Unknown'
      }));
      
      // Store all fetched data
      setSourceData(reportData);
      
      // Apply pagination
      applyPagination(reportData);
    } catch (error) {
      console.error('Error loading source wise tickets report:', error);
      setError(extractErrorMessage(error, 'Failed to load source wise tickets report'));
      toast.error(extractErrorMessage(error, 'Failed to load source wise tickets report'));
      setSourceData([]);
      setFilteredSourceData([]);
      setPaginationData(null);
    } finally {
      setLoading(false);
    }
  };

  const applyPagination = (dataToPaginate) => {
    if (!dataToPaginate || dataToPaginate.length === 0) {
      setFilteredSourceData([]);
      setPaginationData({
        totalElements: 0,
        totalPages: 0,
        pageNumber: currentPage,
        pageSize: pageSize,
        first: true,
        last: true
      });
      return;
    }
    
    const start = currentPage * pageSize;
    const end = start + pageSize;
    const paginatedData = dataToPaginate.slice(start, end);
    
    setFilteredSourceData(paginatedData);
    setPaginationData({
      totalElements: dataToPaginate.length,
      totalPages: Math.ceil(dataToPaginate.length / pageSize),
      pageNumber: currentPage,
      pageSize: pageSize,
      first: currentPage === 0,
      last: end >= dataToPaginate.length
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && (paginationData ? newPage < paginationData.totalPages : true)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Chart data derived from full sourceData (no backend change)
  const pieChartData = useMemo(() =>
    sourceData.map((item) => ({
      name: item.sourceName || item.key || item.source || 'Unknown',
      value: Number(item.totalCount) || 0
    })).filter((d) => d.value > 0),
    [sourceData]
  );

  const barChartData = useMemo(() =>
    sourceData.map((item) => ({
      name: (item.sourceName || item.key || item.source || 'Unknown').replace(/_/g, ' '),
      total: Number(item.totalCount) || 0,
      open: Number(item.openCount) || 0,
      inProgress: Number(item.inProgressCount) || 0,
      resolved: Number(item.resolvedCount) || 0,
      closed: Number(item.closedCount) || 0,
      escalated: Number(item.escalatedCount) || 0
    })),
    [sourceData]
  );

  const fetchSourceDataForExport = async () => {
    const response = await ticketGet('/gateway/api/ticket/analytics/report', { type: 'TICKET_SOURCE' });
    let reportData = [];
    if (response?.success && response?.data) {
      reportData = Array.isArray(response.data) ? response.data : [];
    } else if (response?.data?.success && response?.data?.data) {
      reportData = Array.isArray(response.data.data) ? response.data.data : [];
    } else if (response?.data) {
      reportData = Array.isArray(response.data) ? response.data : [];
    } else if (Array.isArray(response)) {
      reportData = response;
    }
    return reportData.map((item) => ({
      ...item,
      sourceName: item.key || item.source || item.sourceName || 'Unknown'
    }));
  };

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const exportData = await fetchSourceDataForExport();
      if (exportData.length === 0) {
        toast.error('No data to export');
        return;
      }
      const headers = ['Source', 'Total Tickets', 'Open', 'In Progress', 'Resolved', 'Closed', 'Escalated'];
      const rows = exportData.map((r) => [
        r.sourceName || r.key || r.source || 'Unknown',
        r.totalCount ?? 0,
        r.openCount ?? 0,
        r.inProgressCount ?? 0,
        r.resolvedCount ?? 0,
        r.closedCount ?? 0,
        r.escalatedCount ?? 0,
      ]);
      downloadStyledXlsx({
        fileName: `source-wise-tickets-report-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: 'Source Wise Tickets',
        headers,
        rows,
        numberColumns: [1, 2, 3, 4, 5, 6],
        textColumns: [0],
        centerColumns: [1, 2, 3, 4, 5, 6],
      });
      toast.success('Report exported as Excel');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const exportData = await fetchSourceDataForExport();
      if (exportData.length === 0) {
        toast.error('No data to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Source wise Tickets Report', 14, 15);
      doc.setFontSize(10);
      const head = [['Source', 'Total Tickets', 'Open', 'In Progress', 'Resolved', 'Closed', 'Escalated']];
      const body = exportData.map((r) => [
        r.sourceName || r.key || r.source || 'Unknown',
        r.totalCount ?? 0,
        r.openCount ?? 0,
        r.inProgressCount ?? 0,
        r.resolvedCount ?? 0,
        r.closedCount ?? 0,
        r.escalatedCount ?? 0
      ]);
      autoTable(doc, { startY: 22, head, body, styles: { fontSize: 9 } });
      doc.save(`source-wise-tickets-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Report exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Source wise Tickets Report</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View ticket statistics grouped by source (Portal, Email, Agent, etc.)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={loading || exporting}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export'}
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[140px]">
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportExcel}>
                  <FileText className="h-4 w-4" /> Export Excel
                </button>
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportPdf}>
                  <FileText className="h-4 w-4" /> Export PDF
                </button>
              </div>
            )}
          </div>
          <Button
            onClick={loadSourceReport}
            disabled={loading}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Graphical representation: charts from same data as table */}
      {!loading && sourceData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                Tickets by source (distribution)
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Share of total tickets per source
              </p>
            </CardHeader>
            <CardContent>
              {pieChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display.</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="45%"
                      innerRadius={56}
                      outerRadius={88}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${entry.name}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      labelFormatter={(name) => name}
                    />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      wrapperStyle={{ fontSize: '12px' }}
                      formatter={(value, entry) => (
                        <span className="text-foreground">
                          {value} {entry.payload?.value != null ? `(${entry.payload.value})` : ''}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                Tickets by source (counts)
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Total and status breakdown per source
              </p>
            </CardHeader>
            <CardContent>
              {barChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display.</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={barChartData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="name" className="text-xs" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [value]}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="open" stackId="status" fill={CHART_COLORS[0]} name="Open" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="inProgress" stackId="status" fill={CHART_COLORS[1]} name="In Progress" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="resolved" stackId="status" fill={CHART_COLORS[2]} name="Resolved" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="closed" stackId="status" fill={CHART_COLORS[3]} name="Closed" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="escalated" stackId="status" fill={CHART_COLORS[4]} name="Escalated" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Source wise Tickets Report Table Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Ticket Source Statistics
            {paginationData && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({paginationData.totalElements} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading source wise tickets report...</span>
            </div>
          ) : filteredSourceData.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No source data found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Source</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Total Tickets</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Open</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">In Progress</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Resolved</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Closed</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Escalated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSourceData.map((sourceDataItem, index) => (
                      <tr
                        key={sourceDataItem.key || sourceDataItem.source || index}
                        className="border-b border-border hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-3 text-sm text-foreground font-medium">
                          {sourceDataItem.sourceName || sourceDataItem.key || sourceDataItem.source || 'Unknown'}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {sourceDataItem.totalCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {sourceDataItem.openCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {sourceDataItem.inProgressCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {sourceDataItem.resolvedCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {sourceDataItem.closedCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {sourceDataItem.escalatedCount || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {paginationData && paginationData.totalElements > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-border gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {currentPage * pageSize + 1} to{' '}
                    {Math.min((currentPage + 1) * pageSize, paginationData.totalElements)} of{' '}
                    {paginationData.totalElements} sources
                  </div>
                  <div className="flex items-center gap-2">
                    {/* First Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(0)}
                        disabled={currentPage === 0 || loading}
                        className="hidden sm:flex"
                      >
                        First
                      </Button>
                    )}
                    
                    {/* Previous Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0 || loading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                    )}
                    
                    {/* Page Numbers */}
                    {paginationData.totalPages > 0 && (
                      <div className="flex items-center gap-1">
                        {(() => {
                          const pages = [];
                          const totalPages = paginationData.totalPages;
                          const current = currentPage + 1;
                          
                          if (totalPages <= 7) {
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(
                                <Button
                                  key={i}
                                  variant={i === current ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => handlePageChange(i - 1)}
                                  disabled={loading || totalPages === 1}
                                  className="min-w-[2.5rem]"
                                >
                                  {i}
                                </Button>
                              );
                            }
                          } else {
                            pages.push(
                              <Button
                                key={1}
                                variant={current === 1 ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(0)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                1
                              </Button>
                            );
                            
                            if (current > 3) {
                              pages.push(
                                <span key="ellipsis-start" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            const start = Math.max(2, current - 1);
                            const end = Math.min(totalPages - 1, current + 1);
                            
                            for (let i = start; i <= end; i++) {
                              if (i !== 1 && i !== totalPages) {
                                pages.push(
                                  <Button
                                    key={i}
                                    variant={i === current ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => handlePageChange(i - 1)}
                                    disabled={loading}
                                    className="min-w-[2.5rem]"
                                  >
                                    {i}
                                  </Button>
                                );
                              }
                            }
                            
                            if (current < totalPages - 2) {
                              pages.push(
                                <span key="ellipsis-end" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            pages.push(
                              <Button
                                key={totalPages}
                                variant={current === totalPages ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(totalPages - 1)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                {totalPages}
                              </Button>
                            );
                          }
                          
                          return pages;
                        })()}
                      </div>
                    )}
                    
                    {/* Next Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    )}
                    
                    {/* Last Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(paginationData.totalPages - 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                        className="hidden sm:flex"
                      >
                        Last
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SourceWiseTicketsReport;
